// src/pages/admin/AdminProducts/AdminProducts.jsx - UPDATED WITH EDIT SUPPORT
import React, { useState, useEffect } from 'react';
import './AdminProducts.css';
import productsService from '../../../services/products.service';
import categoriesService from '../../../services/categories.service';
import attributesService from '../../../services/attributes.service';
import { ColorModal, MaterialModal } from '../AttributeModals/AttributeModals';
import ProductFormModal from './ProductFormModal';
import { useToast } from '../../../context/ToastContext';

// Stock status functions
export const STOCK_THRESHOLDS = {
  OUT_OF_STOCK: 0,
  LOW_STOCK: 10,
  IN_STOCK: 11,
};

export const getStockStatus = (stockCount) => {
  const count = Number(stockCount) || 0;
  if (count === 0) return 'out-of-stock';
  if (count <= STOCK_THRESHOLDS.LOW_STOCK) return 'low-stock';
  return 'in-stock';
};

export const getStockLabel = (stock, stockCount) => {
  if (stock === 'in-stock') return 'En stock';
  if (stock === 'low-stock') return `Faible (${stockCount})`;
  if (stock === 'out-of-stock') return 'Rupture';
  return stock;
};

const AdminProducts = () => {
  const toast = useToast();
  // Data states
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [colors, setColors] = useState([]);
  const [materials, setMaterials] = useState([]);

  // UI states
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showColorModal, setShowColorModal] = useState(false);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Form states - UPDATED with arrays for colors/materials
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    price: '',
    oldPrice: '',
    images: [],
    rating: 0,
    reviews: 0,
    stock: 'in-stock',
    stockCount: 0,
    badge: '',
    features: [],
    material: '',
    color: '',
    brand: '',
    hasVariants: false,
    variants: [],
    variantTypes: { hasDimensions: false, hasColors: false, hasMaterials: false, hasCompartiments: false }
  });

  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    color: '#3b82f6',
    icon: ''
  });

  const [featureInput, setFeatureInput] = useState('');

  // Variant form state - UPDATED with arrays
  const [variantForm, setVariantForm] = useState({
    dimensions: { width: '', height: '', length: '' },
    colors: [],
    materials: [],
    compartiment: '',
    price: ''
  });

  // Fetch data on mount
  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchColors();
    fetchMaterials();
  }, []);

  // Fetch functions
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const result = await productsService.getAllProducts();
      if (result.success) {
        setProducts(result.products);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const result = await categoriesService.getAllCategories();
      if (result.success) {
        setCategories(result.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchColors = async () => {
    try {
      const result = await attributesService.getAllColors();
      if (result.success) {
        setColors(result.colors);
      }
    } catch (error) {
      console.error('Error fetching colors:', error);
    }
  };

  const fetchMaterials = async () => {
    try {
      const result = await attributesService.getAllMaterials();
      if (result.success) {
        setMaterials(result.materials);
      }
    } catch (error) {
      console.error('Error fetching materials:', error);
    }
  };

  // Modal handlers
  const handleAdd = () => {
    setModalMode('add');
    setFormData({
      name: '',
      category: categories.length > 0 ? categories[0].name : '',
      description: '',
      price: '',
      oldPrice: '',
      images: [],
      rating: 0,
      reviews: 0,
      stock: 'in-stock',
      stockCount: 0,
      badge: '',
      features: [],
      material: '',
      color: '',
      brand: '',
      hasVariants: false,
      variants: [],
      variantTypes: { hasDimensions: false, hasColors: false, hasMaterials: false, hasCompartiments: false }
    });
    setVariantForm({
      dimensions: { width: '', height: '', length: '' },
      colors: [],
      materials: [],
      compartiment: '',
      price: ''
    });
    setShowModal(true);
  };

  const handleEdit = (product) => {
    setModalMode('edit');
    setSelectedProduct(product);

    let imagesArray = [];
    if (Array.isArray(product.images)) {
      imagesArray = product.images;
    } else if (product.image) {
      imagesArray = [typeof product.image === 'object' ? product.image : { url: product.image }];
    }

    setFormData({
      name: product.name || '',
      category: product.category || '',
      description: product.description || '',
      price: product.price || '',
      oldPrice: product.oldPrice || '',
      images: imagesArray,
      rating: product.rating || 0,
      reviews: product.reviews || 0,
      stock: product.stock || 'in-stock',
      stockCount: product.stockCount || 0,
      badge: product.badge || '',
      features: product.features || [],
      material: product.material || '',
      color: product.color || '',
      brand: product.brand || '',
      hasVariants: product.hasVariants || false,
      variants: product.variants || [],
      variantTypes: product.variantTypes || { hasDimensions: false, hasColors: false, hasMaterials: false, hasCompartiments: false }
    });
    setShowModal(true);
  };

  // Input handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'stockCount') {
      const newStockStatus = getStockStatus(value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        stock: newStockStatus
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Image handlers
  const handleImageUpload = (imageData) => {
    if (!imageData) return;

    if (Array.isArray(imageData)) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...imageData]
      }));
    } else if (imageData && typeof imageData === 'object') {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, imageData]
      }));
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSetPrimaryImage = (index) => {
    setFormData(prev => {
      const newImages = [...prev.images];
      const [primaryImage] = newImages.splice(index, 1);
      newImages.unshift(primaryImage);
      return { ...prev, images: newImages };
    });
  };

  // Feature handlers
  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, featureInput.trim()]
      }));
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  // Variant handlers
  const handleVariantTypeChange = (type, checked) => {
    setFormData(prev => ({
      ...prev,
      variantTypes: {
        ...prev.variantTypes,
        [type]: checked
      }
    }));
  };

  const handleVariantFormChange = (field, value) => {
    setVariantForm(prev => ({ ...prev, [field]: value }));
  };

  // UPDATED: handleAddVariant with edit support
  const handleAddVariant = (editIndex = null) => {
    const { variantTypes } = formData;
    const { dimensions, colors, materials, compartiment, price } = variantForm;

    if (!price) {
      toast.showWarning('Le prix est requis pour chaque variante');
      return;
    }

    // Validate at least one variant type is selected and has value
    const hasDimensionValue = variantTypes.hasDimensions &&
      (dimensions.width || dimensions.height || dimensions.length);
    const hasColorValue = variantTypes.hasColors && colors && colors.length > 0;
    const hasMaterialValue = variantTypes.hasMaterials && materials && materials.length > 0;
    const hasCompartimentValue = variantTypes.hasCompartiments && compartiment;

    if (!hasDimensionValue && !hasColorValue && !hasMaterialValue && !hasCompartimentValue) {
      toast.showWarning('Veuillez remplir au moins un type de variante');
      return;
    }

    const variant = { price: Number(price) };

    if (hasDimensionValue) {
      variant.dimensions = { ...dimensions };
    }

    if (hasColorValue) {
      variant.colors = [...colors];
    }

    if (hasMaterialValue) {
      variant.materials = [...materials];
    }

    if (hasCompartimentValue) {
      variant.compartiment = compartiment;
    }

    // Generate variant name
    const nameParts = [];
    if (variant.dimensions) {
      const dims = [];
      if (variant.dimensions.width) dims.push(`L${variant.dimensions.width}`);
      if (variant.dimensions.height) dims.push(`H${variant.dimensions.height}`);
      if (variant.dimensions.length) dims.push(`P${variant.dimensions.length}`);
      if (dims.length > 0) nameParts.push(dims.join('×'));
    }
    if (variant.colors && variant.colors.length > 0) {
      nameParts.push(variant.colors.join('+'));
    }
    if (variant.materials && variant.materials.length > 0) {
      nameParts.push(variant.materials.join('+'));
    }
    if (variant.compartiment) nameParts.push(variant.compartiment);

    variant.name = nameParts.join(' - ');

    // If editing, update the variant at the index
    if (editIndex !== null) {
      setFormData(prev => ({
        ...prev,
        variants: prev.variants.map((v, i) => i === editIndex ? variant : v)
      }));
      toast.showSuccess('Variante modifiée: ' + variant.name);
    } else {
      // Otherwise, add new variant
      setFormData(prev => ({
        ...prev,
        variants: [...prev.variants, variant]
      }));
      toast.showSuccess('Variante ajoutée: ' + variant.name);
    }

    // Reset variant form
    setVariantForm({
      dimensions: { width: '', height: '', length: '' },
      colors: [],
      materials: [],
      compartiment: '',
      price: ''
    });
  };

  const handleRemoveVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  // Attribute modal handlers
  const handleAddColor = async (colorData) => {
    const result = await attributesService.addColor(colorData);
    if (result.success) {
      await fetchColors();
      setShowColorModal(false);
      toast.showSuccess('Couleur ajoutée avec succès!');
    } else {
      toast.showError('Erreur: ' + result.error);
    }
  };

  const handleAddMaterial = async (materialData) => {
    const result = await attributesService.addMaterial(materialData);
    if (result.success) {
      await fetchMaterials();
      setShowMaterialModal(false);
      toast.showSuccess('Matériau ajouté avec succès!');
    } else {
      toast.showError('Erreur: ' + result.error);
    }
  };

  const handleQuickAddCategory = () => {
    setShowCategoryModal(true);
    setCategoryFormData({
      name: '',
      description: '',
      color: '#3b82f6',
      icon: ''
    });
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await categoriesService.addCategory(categoryFormData);
      if (result.success) {
        await fetchCategories();
        setShowCategoryModal(false);
        setFormData(prev => ({ ...prev, category: result.category.name }));
        toast.showSuccess('Catégorie ajoutée avec succès!');
      } else {
        toast.showError('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Error adding category:', error);
      toast.showError('Une erreur est survenue');
    }
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.images || formData.images.length === 0) {
      toast.showWarning('Veuillez télécharger au moins une image pour le produit');
      return;
    }

    // Validation for variants
    if (formData.hasVariants) {
      if (formData.variants.length === 0) {
        toast.showWarning('Veuillez ajouter au moins une variante ou désactiver les variantes');
        return;
      }
    } else {
      // Validate standard fields for non-variant products
      if (!formData.price) {
        toast.showWarning('Le prix est requis');
        return;
      }
      if (!formData.material || !formData.color) {
        toast.showWarning('Le matériau et la couleur sont requis');
        return;
      }
    }

    setLoading(true);

    try {
      const productData = {
        ...formData,
        price: formData.hasVariants ? (formData.variants[0]?.price || 0) : Number(formData.price),
        oldPrice: formData.oldPrice ? Number(formData.oldPrice) : null,
        rating: Number(formData.rating),
        reviews: Number(formData.reviews),
        stockCount: Number(formData.stockCount),
        stock: getStockStatus(formData.stockCount),
        images: formData.images,
        image: formData.images[0]
      };

      // Clean up if no variants
      if (!formData.hasVariants || formData.variants.length === 0) {
        delete productData.variants;
        delete productData.variantTypes;
        productData.hasVariants = false;
      }

      let result;
      if (modalMode === 'add') {
        result = await productsService.addProduct(productData);
      } else {
        result = await productsService.updateProduct(selectedProduct.id, productData);
      }

      if (result.success) {
        await fetchProducts();
        setShowModal(false);
        toast.showSuccess(`Produit ${modalMode === 'add' ? 'ajouté' : 'modifié'} avec succès!`);
      } else {
        toast.showError('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Error submitting product:', error);
      toast.showError('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  // Delete handler
  const handleDelete = async (productId) => {
    if (!deleteConfirm) {
      setDeleteConfirm(productId);
      return;
    }

    setLoading(true);
    try {
      const result = await productsService.deleteProduct(productId);
      if (result.success) {
        await fetchProducts();
        toast.showSuccess('Produit supprimé avec succès!');
      } else {
        toast.showError('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.showError('Une erreur est survenue');
    } finally {
      setLoading(false);
      setDeleteConfirm(null);
    }
  };

  // Computed values
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const getProductImageUrl = (product) => {
    if (Array.isArray(product.images) && product.images.length > 0) {
      const firstImage = product.images[0];
      return typeof firstImage === 'object' ? firstImage.url : firstImage;
    }
    if (typeof product.image === 'object' && product.image?.url) {
      return product.image.url;
    }
    if (typeof product.image === 'string') {
      return product.image;
    }
    return '';
  };

  const materialsByCategory = materials.reduce((acc, material) => {
    if (!acc[material.category]) {
      acc[material.category] = [];
    }
    acc[material.category].push(material);
    return acc;
  }, {});

  return (
    <div className="admin-products">
      {/* Filters */}
      <div className="products-filters">
        <div className="filter-search">
          <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Rechercher un produit..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="filter-select"
        >
          <option value="all">Toutes les catégories</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.name}>{cat.name}</option>
          ))}
        </select>

        <button className="btn btn-primary" onClick={handleAdd}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Ajouter un produit
        </button>
      </div>

      {/* Products Table */}
      {loading ?
        (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Chargement...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            </svg>
            <h3>Aucun produit trouvé</h3>
            <p>Commencez par ajouter votre premier produit</p>
          </div>
        ) : (
          <div className="products-table-container">
            <table className="products-table">
              <thead>
                <tr>
                  <th style={{ width: '50px', textAlign: 'center' }}>#</th>
                  <th>Produit</th>
                  <th>Catégorie</th>
                  <th>Prix</th>
                  <th>Stock</th>
                  <th>Variantes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product, index) => (
                  <tr key={product.id}>
                    <td style={{ textAlign: 'center', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '1rem' }}>
                      {index + 1}
                    </td>
                    <td>
                      <div className="product-cell">
                        <img
                          src={getProductImageUrl(product)}
                          alt={product.name}
                          className="product-thumb"
                        />
                        <div className="product-info">
                          <div className="product-name">{product.name}</div>
                          <div className="product-desc">{product.description?.substring(0, 50)}...</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="category-badge">{product.category}</span>
                    </td>
                    <td>
                      <div className="price-cell">
                        <span className="price">{product.displayPriceText || (product.price.toLocaleString() + ' DA')}</span>
                        {product.oldPrice && !product.hasVariants && (
                          <span className="old-price">{product.oldPrice.toLocaleString()} DA</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`stock-badge ${product.stock}`}>
                        {getStockLabel(product.stock, product.stockCount)}
                      </span>
                    </td>
                    <td>
                      {product.hasVariants && product.variants?.length > 0 ? (
                        <span className="images-count-badge">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="7" height="7" />
                            <rect x="14" y="3" width="7" height="7" />
                            <rect x="14" y="14" width="7" height="7" />
                            <rect x="3" y="14" width="7" height="7" />
                          </svg>
                          {product.variants.length}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>-</span>
                      )}
                    </td>

                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(product)}
                          title="Modifier"
                        >
                          modifier
                        </button>
                        <button
                          className={`btn-icon btn-delete ${deleteConfirm === product.id ? 'confirm' : ''}`}
                          onClick={() => handleDelete(product.id)}
                          title={deleteConfirm === product.id ? 'Cliquer pour confirmer' : 'Supprimer'}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                        {deleteConfirm === product.id && (
                          <button
                            className="btn-icon btn-cancel"
                            onClick={() => setDeleteConfirm(null)}
                            title="Annuler"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {/* Product Form Modal */}
      <ProductFormModal
        show={showModal}
        mode={modalMode}
        formData={formData}
        categories={categories}
        colors={colors}
        materials={materials}
        materialsByCategory={materialsByCategory}
        loading={loading}
        featureInput={featureInput}
        variantForm={variantForm}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
        onImageUpload={handleImageUpload}
        onRemoveImage={handleRemoveImage}
        onSetPrimaryImage={handleSetPrimaryImage}
        onFeatureInputChange={setFeatureInput}
        onAddFeature={addFeature}
        onRemoveFeature={removeFeature}
        onShowCategoryModal={handleQuickAddCategory}
        onShowColorModal={() => setShowColorModal(true)}
        onShowMaterialModal={() => setShowMaterialModal(true)}
        onVariantTypeChange={handleVariantTypeChange}
        onVariantFormChange={handleVariantFormChange}
        onAddVariant={handleAddVariant}
        onRemoveVariant={handleRemoveVariant}
      />

      {/* Color Modal */}
      {showColorModal && (
        <ColorModal
          onClose={() => setShowColorModal(false)}
          onSubmit={handleAddColor}
        />
      )}

      {/* Material Modal */}
      {showMaterialModal && (
        <MaterialModal
          onClose={() => setShowMaterialModal(false)}
          onSubmit={handleAddMaterial}
        />
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
          <div className="product-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ajouter une catégorie</h2>
              <button className="modal-close" onClick={() => setShowCategoryModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCategorySubmit} className="modal-form">
              <div className="form-group">
                <label>Nom de la catégorie *</label>
                <input
                  type="text"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  placeholder="Ex: Bureaux"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={categoryFormData.description}
                  onChange={(e) => setCategoryFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows="3"
                  placeholder="Description de la catégorie"
                />
              </div>

              <div className="form-group">
                <label>Icône (emoji)</label>
                <input
                  type="text"
                  value={categoryFormData.icon}
                  onChange={(e) => setCategoryFormData(prev => ({ ...prev, icon: e.target.value }))}
                  placeholder="Ex: 🪑"
                  maxLength="2"
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCategoryModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;